import { useState, useEffect, useMemo, useRef } from 'react'
import html2canvas from 'html2canvas'
import { supabase } from '../supabase'
import { useToast } from '../hooks/useToast'

function ModalConfirm({ msg, onConfirm, onCancel }) {
  return (
    <div className="modal-overlay" style={{ zIndex: 100 }} onClick={onCancel}>
      <div className="modal" style={{ paddingBottom: 20 }} onClick={e => e.stopPropagation()}>
        <div className="modal-handle" />
        <p style={{ fontSize: 15, marginBottom: 20, textAlign: 'center' }}>{msg}</p>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost" style={{ flex: 1 }} onClick={onCancel}>Cancelar</button>
          <button className="btn btn-danger" style={{ flex: 1 }} onClick={onConfirm}>Remover</button>
        </div>
      </div>
    </div>
  )
}

export default function Arranjos() {
  const [arranjos, setArranjos] = useState([])
  const [itens, setItens] = useState([])
  const [cestos, setCestos] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [viewArranjo, setViewArranjo] = useState(null)
  const [editArranjo, setEditArranjo] = useState(null)
  const [nome, setNome] = useState('')
  const [margem, setMargem] = useState(80)
  const [selecionados, setSelecionados] = useState([])
  const [cestosSelecionados, setCestosSelecionados] = useState([])
  const [precoInput, setPrecoInput] = useState('')
  const [saving, setSaving] = useState(false)
  const [busca, setBusca] = useState('')
  const [filtrocat, setFiltrocat] = useState('')
  const [confirm, setConfirm] = useState(null)
  const [alertaEstoque, setAlertaEstoque] = useState(null)
  const [ordem, setOrdem] = useState('nome')
  const [exportando, setExportando] = useState(false)
  const [fotoFile, setFotoFile] = useState(null)
  const [fotoPreview, setFotoPreview] = useState(null)
  const fichaRef = useRef(null)
  const fotoInputRef = useRef(null)
  const { msg, visible, show } = useToast()

  useEffect(() => { fetchAll() }, [])

  async function fetchAll(manterView = false) {
    setLoading(true)
    const [{ data: arr }, { data: it }, { data: cs }] = await Promise.all([
      supabase.from('arranjos').select('*, arranjo_itens(*, itens(*)), arranjo_cestos(*, cestos(*))').order('created_at', { ascending: false }),
      supabase.from('itens').select('*').order('categoria').order('nome'),
      supabase.from('cestos').select('*').order('nome'),
    ])
    setArranjos(arr || [])
    setItens(it || [])
    setCestos(cs || [])
    // bug 2: atualiza viewArranjo com dados frescos
    if (manterView) {
      setViewArranjo(v => v ? (arr || []).find(a => a.id === v.id) || null : null)
    }
    setLoading(false)
  }

  function abrirNovo() {
    setEditArranjo(null)
    setNome('')
    setMargem(80)
    setSelecionados([])
    setCestosSelecionados([])
    setBusca('')
    setFiltrocat('')
    setPrecoInput('')
    setFotoFile(null)
    setFotoPreview(null)
    setShowForm(true)
  }

  function abrirEditar(arr) {
    setViewArranjo(null)
    setEditArranjo(arr)
    setNome(arr.nome)
    setMargem(arr.margem)
    setSelecionados(
      (arr.arranjo_itens || []).filter(ai => ai.itens).map(ai => ({ item: ai.itens, qty: ai.quantidade }))
    )
    setCestosSelecionados(
      (arr.arranjo_cestos || []).filter(ac => ac.cestos).map(ac => ({ cesto: ac.cestos, qty: ac.quantidade }))
    )
    setBusca('')
    setFiltrocat('')
    setPrecoInput(arr.preco_venda ? parseFloat(arr.preco_venda).toFixed(2) : '')
    setFotoFile(null)
    setFotoPreview(arr.foto_url || null)
    setShowForm(true)
  }

  function toggleCesto(cesto) {
    const existe = cestosSelecionados.find(s => s.cesto.id === cesto.id)
    if (existe) setCestosSelecionados(cestosSelecionados.filter(s => s.cesto.id !== cesto.id))
    else setCestosSelecionados([...cestosSelecionados, { cesto, qty: 1 }])
  }

  function setCestoQty(cestoId, qty) {
    if (qty < 1) return
    setCestosSelecionados(cestosSelecionados.map(s => s.cesto.id === cestoId ? { ...s, qty } : s))
  }

  function toggleItem(item) {
    const existe = selecionados.find(s => s.item.id === item.id)
    if (existe) setSelecionados(selecionados.filter(s => s.item.id !== item.id))
    else setSelecionados([...selecionados, { item, qty: 1 }])
  }

  function setQty(itemId, qty) {
    if (qty < 1) return
    setSelecionados(selecionados.map(s => s.item.id === itemId ? { ...s, qty } : s))
  }

  const { custItens, custCestos, custoMontagem, custoTotal } = useMemo(() => {
    const custItens = selecionados.reduce((sum, s) => sum + s.item.custo * s.qty, 0)
    const custCestos = cestosSelecionados.reduce((sum, s) => sum + s.cesto.custo * s.qty, 0)
    const maoDeObra = selecionados.reduce((sum, s) => sum + s.item.custo * s.qty * ((s.item.margem ?? 10) / 100), 0)
    const custoMontagem = 15 + maoDeObra
    return { custItens, custCestos, custoMontagem, custoTotal: custItens + custCestos + custoMontagem }
  }, [selecionados, cestosSelecionados])

  const precoVenda = useMemo(() => custoTotal * (1 + margem / 100), [custoTotal, margem])

  function handleFotoChange(e) {
    const file = e.target.files[0]
    if (!file) return
    setFotoFile(file)
    setFotoPreview(URL.createObjectURL(file))
  }

  async function uploadFoto(id) {
    if (!fotoFile) return null
    const ext = fotoFile.name.split('.').pop()
    const path = `${id}.${ext}`
    const { error } = await supabase.storage.from('arranjos').upload(path, fotoFile, { upsert: true })
    if (error) { console.error('upload foto arranjo:', error); show('Erro: ' + error.message); return null }
    const { data: { publicUrl } } = supabase.storage.from('arranjos').getPublicUrl(path)
    return publicUrl
  }

  function parsePreco(str) {
    return parseFloat(String(str).replace(',', '.'))
  }

  function handlePrecoBlur() {
    if (precoInput === '') return
    const p = parsePreco(precoInput)
    if (!isNaN(p) && p >= 0) {
      setPrecoInput(p.toFixed(2))
    } else {
      setPrecoInput('')
    }
  }

  const precoFinal = parsePreco(precoInput) || 0
  const margemCalculada = custoTotal > 0 && precoFinal > 0 ? ((precoFinal / custoTotal) - 1) * 100 : margem

  async function salvar() {
    if (!nome.trim()) { show('Dê um nome ao produto'); return }
    if (selecionados.length === 0 && cestosSelecionados.length === 0) { show('Adicione pelo menos um cesto ou item'); return }
    if (precoFinal <= 0) { show('Preço de venda deve ser maior que zero'); return }
    setSaving(true)

    if (editArranjo) {
      const fotoUrl = await uploadFoto(editArranjo.id)
      const updateData = { nome: nome.trim(), margem: margemCalculada, custo_total: custoTotal, preco_venda: precoFinal }
      if (fotoUrl) updateData.foto_url = fotoUrl
      await supabase.from('arranjos').update(updateData).eq('id', editArranjo.id)
      await supabase.from('arranjo_itens').delete().eq('arranjo_id', editArranjo.id)
      await supabase.from('arranjo_cestos').delete().eq('arranjo_id', editArranjo.id)
      if (selecionados.length > 0)
        await supabase.from('arranjo_itens').insert(
          selecionados.map(s => ({ arranjo_id: editArranjo.id, item_id: s.item.id, quantidade: s.qty, custo_unitario: s.item.custo }))
        )
      if (cestosSelecionados.length > 0)
        await supabase.from('arranjo_cestos').insert(
          cestosSelecionados.map(s => ({ arranjo_id: editArranjo.id, cesto_id: s.cesto.id, quantidade: s.qty, custo_unitario: s.cesto.custo }))
        )
        show('✅ Produto atualizado!')
    } else {
      const { data: arr, error } = await supabase
        .from('arranjos')
        .insert([{ nome: nome.trim(), margem: margemCalculada, custo_total: custoTotal, preco_venda: precoFinal }])
        .select().single()
      if (error) { show('Erro ao salvar'); setSaving(false); return }
      const fotoUrl = await uploadFoto(arr.id)
      if (fotoUrl) await supabase.from('arranjos').update({ foto_url: fotoUrl }).eq('id', arr.id)
      if (selecionados.length > 0)
        await supabase.from('arranjo_itens').insert(
          selecionados.map(s => ({ arranjo_id: arr.id, item_id: s.item.id, quantidade: s.qty, custo_unitario: s.item.custo }))
        )
      if (cestosSelecionados.length > 0)
        await supabase.from('arranjo_cestos').insert(
          cestosSelecionados.map(s => ({ arranjo_id: arr.id, cesto_id: s.cesto.id, quantidade: s.qty, custo_unitario: s.cesto.custo }))
        )
      show('✅ Produto salvo!')
    }

    setSaving(false)
    setShowForm(false)
    setEditArranjo(null)
    fetchAll(true)
  }

  async function duplicarArranjo(arr) {
    const { data: novo, error } = await supabase
      .from('arranjos')
      .insert([{ nome: arr.nome + ' (cópia)', margem: arr.margem, custo_total: arr.custo_total, preco_venda: arr.preco_venda }])
      .select().single()
    if (error) { show('Erro ao duplicar'); return }
    if (arr.arranjo_itens?.length > 0)
      await supabase.from('arranjo_itens').insert(
        arr.arranjo_itens.map(ai => ({ arranjo_id: novo.id, item_id: ai.item_id, quantidade: ai.quantidade, custo_unitario: ai.custo_unitario }))
      )
    if (arr.arranjo_cestos?.length > 0)
      await supabase.from('arranjo_cestos').insert(
        arr.arranjo_cestos.map(ac => ({ arranjo_id: novo.id, cesto_id: ac.cesto_id, quantidade: ac.quantidade, custo_unitario: ac.custo_unitario }))
      )
    show('✅ Produto duplicado!')
    setViewArranjo(null)
    fetchAll()
  }

  async function deletarArranjo(id) {
    const [r1, r2, r3] = await Promise.all([
      supabase.from('arranjo_itens').delete().eq('arranjo_id', id),
      supabase.from('arranjo_cestos').delete().eq('arranjo_id', id),
    ])
    const { error } = await supabase.from('arranjos').delete().eq('id', id)
    if (error) { show('Erro ao remover produto'); setConfirm(null); return }
    show('Produto removido')
    setViewArranjo(null)
    setConfirm(null)
    fetchAll()
  }

  async function exportarImagem() {
    if (!fichaRef.current) return
    setExportando(true)
    try {
      const canvas = await html2canvas(fichaRef.current, { scale: 2, backgroundColor: '#fff9fb' })
      const blob = await new Promise(r => canvas.toBlob(r, 'image/png'))
      const file = new File([blob], `${viewArranjo.nome}.png`, { type: 'image/png' })
      if (navigator.share && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: viewArranjo.nome })
      } else {
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `${viewArranjo.nome}.png`
        a.click()
        URL.revokeObjectURL(url)
      }
    } catch (e) {
      show('Erro ao gerar imagem')
    }
    setExportando(false)
  }

  function itensZerados(arr) {
    return (arr.arranjo_itens || [])
      .filter(ai => ai.itens && ai.itens.movimenta_estoque !== false && ai.itens.estoque !== null && ai.itens.estoque <= 0)
      .map(ai => ai.itens.nome)
  }

  function calcCustoVivo(arr) {
    const ais = arr.arranjo_itens || []
    const acs = arr.arranjo_cestos || []
    const custItens = ais.reduce((sum, ai) => sum + (ai.itens?.custo ?? ai.custo_unitario) * ai.quantidade, 0)
    const custCestos = acs.reduce((sum, ac) => sum + (ac.cestos?.custo ?? ac.custo_unitario) * ac.quantidade, 0)
    const maoDeObra = ais.reduce((sum, ai) => sum + (ai.itens?.custo ?? ai.custo_unitario) * ai.quantidade * ((ai.itens?.margem ?? 10) / 100), 0)
    return custItens + custCestos + 15 + maoDeObra
  }

  const categorias = [...new Set(itens.map(i => i.categoria))]

  const arranjosSorted = [...arranjos].sort((a, b) => {
    if (ordem === 'nome') return a.nome.localeCompare(b.nome)
    if (ordem === 'preco_asc') return a.preco_venda - b.preco_venda
    if (ordem === 'preco_desc') return b.preco_venda - a.preco_venda
    return 0
  })

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1 className="page-title" style={{ margin: 0 }}>Produtos</h1>
        <button className="btn btn-primary btn-sm" onClick={abrirNovo} disabled={itens.length === 0 && cestos.length === 0}>
          + Novo
        </button>
      </div>


      {itens.length === 0 && cestos.length === 0 && !loading && (
        <div className="card" style={{ background: '#fff8e1', borderColor: '#ffe082' }}>
          <p style={{ fontSize: 14, color: '#5d4037' }}>
            ⚠️ Cadastre itens ou cestos primeiro para montar produtos.
          </p>
        </div>
      )}

      {loading && <div className="spinner">Carregando...</div>}

      {!loading && arranjos.length === 0 && (itens.length > 0 || cestos.length > 0) && (
        <div className="empty">
          <div className="empty-icon">💐</div>
          <p>Nenhum produto ainda.<br />Crie seu primeiro!</p>
          <br />
          <button className="btn btn-ghost btn-sm" onClick={abrirNovo}>Criar produto</button>
        </div>
      )}

      {!loading && arranjos.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
          <select
            value={ordem}
            onChange={e => setOrdem(e.target.value)}
            style={{ fontSize: 13, border: '1px solid var(--border)', borderRadius: 8, padding: '6px 10px', background: 'white', color: 'var(--muted)' }}
          >
            <option value="nome">Nome A-Z</option>
            <option value="preco_asc">Preço ↑</option>
            <option value="preco_desc">Preço ↓</option>
          </select>
        </div>
      )}

      {arranjosSorted.map(arr => (
        <div key={arr.id} className="arr-card" onClick={() => setViewArranjo(arr)} style={{ cursor: 'pointer' }}>
          <div className="arr-card-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {arr.foto_url && <img src={arr.foto_url} alt="" style={{ width: 40, height: 40, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />}
              <div className="arr-card-name">{arr.nome}</div>
            </div>
            <div>
              <div className="arr-card-price">custo R${calcCustoVivo(arr).toFixed(2)}</div>
              <div className="arr-card-venda">venda R${parseFloat(arr.preco_venda).toFixed(2)}</div>
            </div>
          </div>
          <div className="arr-card-itens">
            {[
              ...(arr.arranjo_cestos || []).map(ac => `🧺 ${ac.cestos?.nome}`),
              ...(arr.arranjo_itens || []).map(ai => `${ai.quantidade}x ${ai.itens?.nome}`)
            ].join(' · ')}
          </div>
          {itensZerados(arr).length > 0 && (
            <div
              style={{ marginTop: 6, fontSize: 12, color: '#c2185b', fontWeight: 600, display: 'inline-block' }}
              onClick={e => { e.stopPropagation(); setAlertaEstoque({ nome: arr.nome, itens: itensZerados(arr) }) }}
            >
              ⚠️ Estoque insuficiente
            </div>
          )}
        </div>
      ))}

      {/* MODAL NOVO / EDITAR */}
      {showForm && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowForm(false)}>
          <div className="modal">
            <div className="modal-handle" />
            <div className="modal-title">{editArranjo ? 'Editar produto' : 'Novo produto'}</div>

            {/* Foto */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <div
                onClick={() => fotoInputRef.current.click()}
                style={{
                  width: 72, height: 72, borderRadius: 12, flexShrink: 0,
                  background: 'var(--bg)', border: '1px dashed var(--border)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', overflow: 'hidden',
                }}
              >
                {fotoPreview
                  ? <img src={fotoPreview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : <span style={{ fontSize: 24 }}>📷</span>
                }
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>Foto do produto</div>
                <button className="btn btn-ghost btn-sm" onClick={() => fotoInputRef.current.click()}>
                  {fotoPreview ? 'Trocar foto' : 'Adicionar foto'}
                </button>
              </div>
              <input ref={fotoInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFotoChange} />
            </div>

            <div className="field">
              <label>Nome do produto</label>
              <input
                placeholder="Ex: Buquê médio de rosas"
                value={nome}
                onChange={e => setNome(e.target.value)}
              />
            </div>

            {cestos.length > 0 && (
              <>
                <div className="section-label">Cestos</div>
                {cestos.map(cesto => {
                  const sel = cestosSelecionados.find(s => s.cesto.id === cesto.id)
                  return (
                    <div key={cesto.id} style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      padding: '8px 10px', borderRadius: 8, marginBottom: 2,
                      background: sel ? 'var(--pink-light)' : 'transparent', transition: 'background 0.15s',
                    }}>
                      <input type="checkbox" checked={!!sel} onChange={() => toggleCesto(cesto)}
                        style={{ width: 18, height: 18, accentColor: 'var(--pink)', cursor: 'pointer', flexShrink: 0 }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: sel ? 600 : 500 }}>🧺 {cesto.nome}</div>
                        <div style={{ fontSize: 12, color: '#888' }}>R${parseFloat(cesto.custo).toFixed(2)}</div>
                      </div>
                      {sel && (
                        <div className="qty-ctrl">
                          <button className="qty-btn" onClick={() => setCestoQty(cesto.id, sel.qty - 1)}>−</button>
                          <span className="qty-num">{sel.qty}</span>
                          <button className="qty-btn" onClick={() => setCestoQty(cesto.id, sel.qty + 1)}>+</button>
                        </div>
                      )}
                      {sel && <div style={{ fontSize: 12, color: 'var(--pink-dark)', fontWeight: 600, minWidth: 52, textAlign: 'right' }}>R${(cesto.custo * sel.qty).toFixed(2)}</div>}
                    </div>
                  )
                })}
              </>
            )}

            <div className="section-label">Itens avulsos</div>
            <div className="row" style={{ marginBottom: 12 }}>
              <div className="field" style={{ marginBottom: 0 }}>
                <input placeholder="Buscar..." value={busca} onChange={e => setBusca(e.target.value)} />
              </div>
              <div className="field" style={{ marginBottom: 0 }}>
                <select value={filtrocat} onChange={e => setFiltrocat(e.target.value)}>
                  <option value="">Todas</option>
                  {categorias.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>

            {categorias.filter(cat => filtrocat === '' || cat === filtrocat).map(cat => {
              const itensDaCat = itens.filter(i =>
                i.categoria === cat && i.nome.toLowerCase().includes(busca.toLowerCase())
              )
              if (itensDaCat.length === 0) return null
              return (
                <div key={cat}>
                  <div style={{ fontSize: 11, color: '#aaa', fontWeight: 600, textTransform: 'uppercase', margin: '10px 0 4px' }}>{cat}</div>
                  {itensDaCat.map(item => {
                    const sel = selecionados.find(s => s.item.id === item.id)
                    return (
                      <div key={item.id} style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '8px 10px', borderRadius: 8, marginBottom: 2,
                        background: sel ? 'var(--pink-light)' : 'transparent', transition: 'background 0.15s',
                      }}>
                        <input type="checkbox" checked={!!sel} onChange={() => toggleItem(item)}
                          style={{ width: 18, height: 18, accentColor: 'var(--pink)', cursor: 'pointer', flexShrink: 0 }} />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 14, fontWeight: sel ? 600 : 500 }}>{item.nome}</div>
                          <div style={{ fontSize: 12, color: '#888' }}>R${parseFloat(item.custo).toFixed(2)} / {item.unidade}</div>
                        </div>
                        {sel && (
                          <div className="qty-ctrl">
                            <button className="qty-btn" onClick={() => setQty(item.id, sel.qty - 1)}>−</button>
                            <span className="qty-num">{sel.qty}</span>
                            <button className="qty-btn" onClick={() => setQty(item.id, sel.qty + 1)}>+</button>
                          </div>
                        )}
                        {sel && <div style={{ fontSize: 12, color: 'var(--pink-dark)', fontWeight: 600, minWidth: 52, textAlign: 'right' }}>R${(item.custo * sel.qty).toFixed(2)}</div>}
                      </div>
                    )
                  })}
                </div>
              )
            })}

            {(selecionados.length > 0 || cestosSelecionados.length > 0) && (
              <div className="cost-box" style={{ marginTop: 16 }}>
                {cestosSelecionados.map(s => (
                  <div className="cost-row" key={s.cesto.id}>
                    <span>🧺 {s.qty}x {s.cesto.nome}</span>
                    <span>R${(s.cesto.custo * s.qty).toFixed(2)}</span>
                  </div>
                ))}
                {selecionados.map(s => (
                  <div className="cost-row" key={s.item.id}>
                    <span>{s.qty}x {s.item.nome}</span>
                    <span>R${(s.item.custo * s.qty).toFixed(2)}</span>
                  </div>
                ))}
                <div className="cost-row" style={{ color: '#888', borderTop: '1px solid #f0e0e8', marginTop: 4, paddingTop: 4 }}>
                  <span>Montagem (serviço + mão de obra)</span>
                  <span>R${custoMontagem.toFixed(2)}</span>
                </div>
                <div className="cost-row total"><span>Custo total</span><span>R${custoTotal.toFixed(2)}</span></div>
              </div>
            )}

            <div className="section-label" style={{ marginTop: 16 }}>Preço de venda</div>
            <div className="row" style={{ marginBottom: 8, alignItems: 'flex-end' }}>
              <div className="field" style={{ marginBottom: 0 }}>
                <label>Valor (R$)</label>
                <input
                  type="text" inputMode="decimal"
                  value={precoInput}
                  onChange={e => setPrecoInput(e.target.value)}
                  onFocus={e => e.target.select()}
                  onBlur={handlePrecoBlur}
                />
                {custoTotal > 0 && (
                  <div style={{ fontSize: 11, color: 'var(--pink-dark)', marginTop: 4 }}>
                    mín. 40% margem: R${(custoTotal / 0.6).toFixed(2)}
                  </div>
                )}
              </div>
              {custoTotal > 0 && precoFinal > 0 && (
                <div style={{ display: 'flex', gap: 8, paddingBottom: 2 }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 10, color: '#aaa', fontWeight: 600, textTransform: 'uppercase', marginBottom: 2 }}>Margem</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--pink-dark)' }}>
                      {(((precoFinal - custoTotal) / precoFinal) * 100).toFixed(1)}%
                    </div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 10, color: '#aaa', fontWeight: 600, textTransform: 'uppercase', marginBottom: 2 }}>Markup</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--green)' }}>
                      {(((precoFinal - custoTotal) / custoTotal) * 100).toFixed(1)}%
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 0 }}>
              <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setShowForm(false)}>Cancelar</button>
              <button className="btn btn-primary" style={{ flex: 2 }} onClick={salvar} disabled={saving}>
                {saving ? 'Salvando...' : editArranjo ? 'Salvar alterações' : 'Salvar produto'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL VER PRODUTO */}
      {viewArranjo && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setViewArranjo(null)}>
          <div className="modal">
            <div className="modal-handle" />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
              <div className="modal-title" style={{ margin: 0 }}>{viewArranjo.nome}</div>
              <button style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#bbb' }} onClick={() => setViewArranjo(null)}>×</button>
            </div>

            {/* FICHA PRA EXPORTAR */}
            <div ref={fichaRef} style={{
              background: 'white', borderRadius: 16, overflow: 'hidden', marginBottom: 12,
              border: '1px solid #f0e0e8', fontFamily: '-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif'
            }}>
              {/* cabeçalho rosa */}
              <div style={{ background: '#c2185b', padding: '18px 20px 14px', display: 'flex', alignItems: 'center', gap: 14 }}>
                {viewArranjo.foto_url && (
                  <img src={viewArranjo.foto_url} alt="" style={{ width: 64, height: 64, borderRadius: 10, objectFit: 'cover', flexShrink: 0, border: '2px solid rgba(255,255,255,0.3)' }} />
                )}
                <div>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.75)', fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>❀ Flor de Sarah</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: 'white' }}>{viewArranjo.nome}</div>
                </div>
              </div>

              {/* composição */}
              <div style={{ padding: '14px 20px' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 10 }}>Composição</div>

                {viewArranjo.arranjo_cestos?.map(ac => (
                  <div key={ac.id} style={{ padding: '6px 0', borderBottom: '1px solid #f5eaee', fontSize: 14, fontWeight: 500 }}>
                    🧺 {ac.cestos?.nome} × {ac.quantidade}
                  </div>
                ))}
                {viewArranjo.arranjo_itens?.map(ai => (
                  <div key={ai.id} style={{ padding: '6px 0', borderBottom: '1px solid #f5eaee', fontSize: 14, fontWeight: 500 }}>
                    {ai.itens?.nome} × {ai.quantidade}
                  </div>
                ))}

                {/* preço destaque */}
                <div style={{
                  marginTop: 14, background: '#fce4ec', borderRadius: 12,
                  padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: '#880e4f' }}>Preço de venda</span>
                  <span style={{ fontSize: 24, fontWeight: 800, color: '#c2185b' }}>
                    R${parseFloat(viewArranjo.preco_venda).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button className="btn btn-danger" style={{ flex: 1 }}
                onClick={() => setConfirm({ msg: `Remover "${viewArranjo.nome}"?`, fn: () => deletarArranjo(viewArranjo.id) })}>
                Remover
              </button>
              <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => duplicarArranjo(viewArranjo)}>
                Duplicar
              </button>
              <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => abrirEditar(viewArranjo)}>
                ✏️ Editar
              </button>
              <button className="btn btn-ghost" style={{ flex: 1 }} onClick={exportarImagem} disabled={exportando}>
                {exportando ? '...' : '📤 Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {alertaEstoque && (
        <div className="modal-overlay" onClick={() => setAlertaEstoque(null)}>
          <div className="modal" style={{ paddingBottom: 20 }} onClick={e => e.stopPropagation()}>
            <div className="modal-handle" />
            <div className="modal-title" style={{ color: '#c2185b' }}>⚠️ Estoque insuficiente</div>
            <p style={{ fontSize: 14, color: '#666', marginBottom: 16 }}>
              Para montar <strong>{alertaEstoque.nome}</strong> faltam:
            </p>
            {alertaEstoque.itens.map(nome => (
              <div key={nome} style={{
                padding: '10px 14px', marginBottom: 6, borderRadius: 8,
                background: '#fce4ec', fontSize: 14, fontWeight: 600, color: '#c2185b'
              }}>
                {nome}
              </div>
            ))}
            <button className="btn btn-ghost" style={{ marginTop: 8, width: '100%' }} onClick={() => setAlertaEstoque(null)}>
              Fechar
            </button>
          </div>
        </div>
      )}

      {confirm && <ModalConfirm msg={confirm.msg} onConfirm={confirm.fn} onCancel={() => setConfirm(null)} />}

      <div className={`toast ${visible ? 'show' : ''}`}>{msg}</div>
    </div>
  )
}
